'use client';

import {
  cn,
  Div,
  noop,
  PressableProps,
  Span,
  useCallbackRef,
} from '@scatterlab/zeta-core';
import { ComponentRef, memo, RefAttributes } from 'react';

import { SelectedBoxIcon, UnselectedBoxIcon } from '../icons';

export type CheckBoxProps = {
  label?: string;
  labelClassName?: string;
  className?: string;
  iconClassName?: string;
  unselectedIconClassName?: string;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  onBlur?: () => void;
  tabIndex?: number;
} & RefAttributes<ComponentRef<typeof Div>>;

const CheckBox = ({
  label,
  labelClassName,
  className,
  iconClassName,
  unselectedIconClassName,
  checked,
  disabled,
  onChange,
  onBlur,
  tabIndex,
  ref,
}: CheckBoxProps) => {
  const handlePress = useCallbackRef<Required<PressableProps>['onPress']>(
    (event) => {
      if (onChange) {
        event.stopPropagation();
        event.preventDefault();
        onChange(!checked);
      }
    },
  );
  return (
    <Div
      ref={ref}
      className={cn('flex cursor-pointer flex-row items-center', className)}
      onPress={disabled ? noop : handlePress}
      onPressOut={onBlur}
      tabIndex={tabIndex}
    >
      {checked ? (
        <SelectedBoxIcon className={cn('size-4 text-white', iconClassName)} />
      ) : (
        <UnselectedBoxIcon
          className={cn(
            'size-4 text-gray-700',
            iconClassName,
            unselectedIconClassName,
          )}
        />
      )}
      {label && (
        <Span
          className={cn('button14 ml-2 select-none text-white', labelClassName)}
        >
          {label}
        </Span>
      )}
    </Div>
  );
};

export default memo(CheckBox);
